import { FCC, Id, isSuccess } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useSearchParams } from "react-router";
import { filterService } from "../api/filter";
import { FilterableEntity, FilterableItem, OnDemandFilterFormValues } from "./filterTypes";
import { useCreateOnDemandFilter } from "./useCreateOnDemandFilter";

interface FilterProviderState {
	entity: FilterableEntity;
	activeFilterId: Id | null;
	resetFilter: () => void;
	options: FilterableItem[];
	applyFilter: (filter: OnDemandFilterFormValues) => Promise<boolean>;
}

const FilterContext = createContext<FilterProviderState | undefined>(undefined);

interface Props {
	entity: FilterableEntity;
	keysToInvalidate?: string[][];
}

export const useFilterableOptionsQuery = (entity: FilterableEntity) => {
	const { data } = useQuery({
		queryKey: filterService.getOperatorOptionsKey,
		queryFn: () => filterService.getFilterableFields(entity).then((r) => r.data.data),
		staleTime: 0,
	});

	return {
		data: data ? data : [],
	};
};

export const FilterProvider: FCC<Props> = ({ children, entity, keysToInvalidate }) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { createOnDemandFilter } = useCreateOnDemandFilter(keysToInvalidate);
	const { data: filterableOptions } = useFilterableOptionsQuery(entity);

	const activeFilterId = useMemo(() => {
		const id = searchParams.get("filterId");
		return id ? +id : null;
	}, [searchParams]);

	const resetFilter = useCallback(
		() =>
			setSearchParams((prev) => {
				prev.delete("filterId");
				return prev;
			}),
		[setSearchParams],
	);

	const options: FilterableItem[] = useMemo(
		() =>
			filterableOptions?.map((option) => ({
				...option,
				entity: entity,
			})) || [],
		[filterableOptions, entity],
	);

	const applyFilter = useCallback(
		async (filter: OnDemandFilterFormValues) => {
			const result = await createOnDemandFilter(entity, filter);

			if (isSuccess(result)) {
				const id = result.data.data.id;

				setSearchParams((prev) => {
					prev.set("filterId", `${id}`);
					return prev;
				});

				return true;
			}
			return false;
		},
		[createOnDemandFilter, entity, setSearchParams],
	);

	return (
		<FilterContext.Provider
			value={{
				entity,
				activeFilterId,
				options,
				resetFilter,
				applyFilter,
			}}
		>
			{children}
		</FilterContext.Provider>
	);
};

export const useFilter = () => {
	const ctx = useContext(FilterContext);

	if (ctx === undefined) {
		throw new Error("useFilter needs to be wrapped inside FilterProvider");
	}

	return ctx;
};
